import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config/api';
import { useAuth } from '../context/AuthContext';
import '../styles/Batch.css';

interface ProgressInfo {
  completed: number; // In progress + completed count
  total: number;
  percentage: number;
}

interface BatchData {
  _id: string;
  handle: string;
  primaryPlatform: string;
  averageCollaborationViews: number;
  lastCollaborationViews: number;
  batchPrice: number;
  batchSize: number;
  batchCount: number;
  batchInterval: number;
  collaborationId: {
    _id: string;
    email: string;
    agreedPrice: number;
  };
  status?: 'pending' | 'negotiating' | 'accepted' | 'rejected' | 'completed';
  negotiatedPrice?: number;
  responseDate?: string;
  isManual?: boolean;
  createdAt: string;
  updatedAt: string;
  progressInfo?: ProgressInfo;
}

const Batch: React.FC = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [batches, setBatches] = useState<BatchData[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'tiktok' | 'instagram'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'negotiating' | 'accepted' | 'rejected' | 'completed'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedBatches, setSelectedBatches] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [resendingEmails, setResendingEmails] = useState(false);

  useEffect(() => {
    fetchBatches();
  }, []);

  const fetchBatches = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/batch`, {
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      const result = await response.json();
      
      if (result.success) {
        setBatches(result.data);
      }
    } catch (error) {
      console.error('Failed to fetch batches:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteBatch = async (batchId: string) => {
    if (!window.confirm('Are you sure you want to delete this batch?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/batch/${batchId}`, {
        method: 'DELETE',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      const result = await response.json();
      
      if (result.success) {
        setBatches(batches.filter(b => b._id !== batchId));
      }
    } catch (error) {
      console.error('Failed to delete batch:', error);
    }
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const toggleSelectBatch = (batchId: string) => {
    const newSelected = new Set(selectedBatches);
    if (newSelected.has(batchId)) {
      newSelected.delete(batchId);
    } else {
      newSelected.add(batchId);
    }
    setSelectedBatches(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedBatches(new Set());
    } else {
      setSelectedBatches(new Set(filteredBatches.map(b => b._id)));
    }
    setSelectAll(!selectAll);
  };

  const filteredBatches = batches
    .filter(batch => {
      // Platform filter
      if (filter !== 'all' && batch.primaryPlatform !== filter) return false;

      // Status filter
      if (statusFilter !== 'all' && batch.status !== statusFilter) return false;

      // Search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const handleMatch = batch.handle.toLowerCase().includes(search);
        const emailMatch = batch.collaborationId?.email?.toLowerCase().includes(search);
        const platformMatch = batch.primaryPlatform.toLowerCase().includes(search);
        return handleMatch || emailMatch || platformMatch;
      }

      return true;
    })
    .sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'handle':
          aValue = a.handle.toLowerCase();
          bValue = b.handle.toLowerCase();
          break;
        case 'batchPrice':
          aValue = a.batchPrice;
          bValue = b.batchPrice;
          break;
        case 'totalValue':
          aValue = a.batchPrice * a.batchSize;
          bValue = b.batchPrice * b.batchSize;
          break;
        case 'batchSize':
          aValue = a.batchSize;
          bValue = b.batchSize;
          break;
        case 'status':
          aValue = a.status || '';
          bValue = b.status || '';
          break;
        case 'createdAt':
        default:
          aValue = new Date(a.createdAt).getTime();
          bValue = new Date(b.createdAt).getTime();
          break;
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  const getPlatformIcon = (platform: string) => {
    switch(platform.toLowerCase()) {
      case 'tiktok': return '📱';
      case 'instagram': return '📷';
      case 'youtube': return '📺';
      default: return '🌐';
    }
  };

  const getSocialMediaUrl = (platform: string, handle: string) => {
    const cleanHandle = handle.replace('@', '');
    switch(platform.toLowerCase()) {
      case 'tiktok':
        return `https://www.tiktok.com/@${cleanHandle}`;
      case 'instagram':
        return `https://www.instagram.com/${cleanHandle}`;
      case 'youtube':
        return `https://www.youtube.com/@${cleanHandle}`;
      default:
        return '#';
    }
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const handleResendPendingEmails = async () => {
    const pendingCount = batches.filter(b => b.status === 'pending').length;

    if (pendingCount === 0) {
      alert('No pending batches to resend emails to.');
      return;
    }

    if (!window.confirm(`Are you sure you want to resend emails to ${pendingCount} pending batch${pendingCount > 1 ? 'es' : ''}?`)) {
      return;
    }

    setResendingEmails(true);
    try {
      const response = await fetch(`${API_URL}/api/batch/resend-pending-emails`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      const result = await response.json();

      if (result.success) {
        const { sent, duplicates, failed, errors } = result.results;
        let message = `Email resend completed!\n\n`;
        message += `✅ Sent: ${sent}\n`;
        if (duplicates > 0) message += `⚠️ Duplicates skipped: ${duplicates}\n`;
        if (failed > 0) {
          message += `❌ Failed: ${failed}\n`;
          if (errors && errors.length > 0) {
            message += `\nFailure reasons:\n`;
            errors.forEach((error: string, index: number) => {
              message += `${index + 1}. ${error}\n`;
            });
          }
        }

        alert(message);

        // Refresh batch list
        fetchBatches();
      } else {
        alert(`Failed to resend emails: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error resending emails:', error);
      alert('Failed to resend emails. Please try again.');
    } finally {
      setResendingEmails(false);
    }
  };

  const getStatusBadge = (status?: string) => {
    switch(status) {
      case 'pending':
        return <span className="status-badge pending">⏳ Pending</span>;
      case 'negotiating':
        return <span className="status-badge negotiating">💬 Negotiating</span>;
      case 'accepted':
        return <span className="status-badge accepted">✅ Accepted</span>;
      case 'rejected':
        return <span className="status-badge rejected">❌ Declined</span>;
      case 'completed':
        return <span className="status-badge completed" style={{ background: 'rgba(107, 114, 128, 0.1)', color: '#4b5563' }}>✔️ Completed</span>;
      default:
        return null;
    }
  };

  return (
    <div className="batch-container">
      <div className="batch-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1>Batch Collaborations</h1>
          <button
            className="manual-register-button"
            onClick={() => navigate('/batch/manual-register')}
            style={{
              padding: '10px 20px',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontWeight: '600',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            + Register Manual Batch
          </button>
        </div>
        <div className="header-stats">
          <span className="stat-item">
            Total Batches: <strong>{batches.length}</strong>
          </span>
          <span className="stat-item">
            Total Value: <strong>${batches.reduce((sum, b) => sum + (b.batchPrice * b.batchSize), 0).toLocaleString()}</strong>
          </span>
        </div>
      </div>

      {/* Stats Dashboard */}
      <div className="stats-dashboard">
        <div 
          className={`stat-card clickable ${statusFilter === 'all' ? 'active' : ''}`}
          onClick={() => setStatusFilter('all')}
        >
          <div className="stat-header">
            <div className="stat-info">
              <h3>All Batches</h3>
              <div className="stat-value">{batches.length}</div>
            </div>
            <div className="stat-icon" style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' }}>
              📊
            </div>
          </div>
        </div>

        <div className="stat-card non-clickable">
          <div className="stat-header">
            <div className="stat-info">
              <h3>Average Price</h3>
              <div className="stat-value">
                ${batches.length > 0 ? Math.round(batches.reduce((sum, b) => sum + b.batchPrice, 0) / batches.length) : 0}
              </div>
            </div>
            <div className="stat-icon" style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' }}>
              📈
            </div>
          </div>
        </div>

        <div
          className={`stat-card clickable ${statusFilter === 'pending' ? 'active' : ''}`}
          onClick={() => setStatusFilter('pending')}
        >
          <div className="stat-header">
            <div className="stat-info">
              <h3>Pending</h3>
              <div className="stat-value">{batches.filter(b => b.status === 'pending').length}</div>
              {batches.filter(b => b.status === 'pending').length > 0 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleResendPendingEmails();
                  }}
                  disabled={resendingEmails}
                  style={{
                    marginTop: '8px',
                    padding: '6px 12px',
                    background: resendingEmails ? '#d1d5db' : '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: resendingEmails ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    opacity: resendingEmails ? 0.7 : 1
                  }}
                >
                  <span>📧</span>
                  {resendingEmails ? 'Sending...' : 'Resend All Emails'}
                </button>
              )}
            </div>
            <div className="stat-icon" style={{ background: 'rgba(251, 191, 36, 0.1)', color: '#f59e0b' }}>
              ⏳
            </div>
          </div>
        </div>

        <div 
          className={`stat-card clickable ${statusFilter === 'negotiating' ? 'active' : ''}`}
          onClick={() => setStatusFilter('negotiating')}
        >
          <div className="stat-header">
            <div className="stat-info">
              <h3>Negotiating</h3>
              <div className="stat-value">{batches.filter(b => b.status === 'negotiating').length}</div>
            </div>
            <div className="stat-icon" style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' }}>
              💬
            </div>
          </div>
        </div>

        <div 
          className={`stat-card clickable ${statusFilter === 'accepted' ? 'active' : ''}`}
          onClick={() => setStatusFilter('accepted')}
        >
          <div className="stat-header">
            <div className="stat-info">
              <h3>Accepted</h3>
              <div className="stat-value">{batches.filter(b => b.status === 'accepted').length}</div>
            </div>
            <div className="stat-icon" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>
              ✅
            </div>
          </div>
        </div>

        <div 
          className={`stat-card clickable ${statusFilter === 'rejected' ? 'active' : ''}`}
          onClick={() => setStatusFilter('rejected')}
        >
          <div className="stat-header">
            <div className="stat-info">
              <h3>Rejected</h3>
              <div className="stat-value">{batches.filter(b => b.status === 'rejected').length}</div>
            </div>
            <div className="stat-icon" style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' }}>
              ❌
            </div>
          </div>
        </div>

        <div 
          className={`stat-card clickable ${statusFilter === 'completed' ? 'active' : ''}`}
          onClick={() => setStatusFilter('completed')}
        >
          <div className="stat-header">
            <div className="stat-info">
              <h3>Completed</h3>
              <div className="stat-value">{batches.filter(b => b.status === 'completed').length}</div>
            </div>
            <div className="stat-icon" style={{ background: 'rgba(107, 114, 128, 0.1)', color: '#6b7280' }}>
              ✔️
            </div>
          </div>
        </div>
      </div>

      {/* Filter Section */}
      <div className="filters-section">
        <div className="filters-row">
          <div className="search-box">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search by creator, email, or platform..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          
          <div className="platform-filter-group">
            <button
              className={`platform-filter-btn ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              🌐 All Platforms
            </button>
            <button
              className={`platform-filter-btn ${filter === 'tiktok' ? 'active' : ''}`}
              onClick={() => setFilter('tiktok')}
            >
              📱 TikTok
            </button>
            <button
              className={`platform-filter-btn ${filter === 'instagram' ? 'active' : ''}`}
              onClick={() => setFilter('instagram')}
            >
              📷 Instagram
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading batches...</div>
      ) : (
        <div className="table-container">
          {filteredBatches.length === 0 ? (
            <div className="no-batches">
              <p>No batches found</p>
              {searchTerm && <span className="hint">Try adjusting your search criteria</span>}
              {!searchTerm && <span className="hint">Create batches from the Collaborations page</span>}
            </div>
          ) : (
            <table className="batch-table">
              <thead>
                <tr>
                  <th style={{ width: '50px' }}>
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={toggleSelectAll}
                      className="checkbox"
                    />
                  </th>
                  <th
                    className="sortable"
                    onClick={() => handleSort('handle')}
                  >
                    <div className="th-content">
                      <span>Creator</span>
                      {sortBy === 'handle' && (
                        <span className="sort-indicator">
                          {sortOrder === 'asc' ? ' ↑' : ' ↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th>Email</th>
                  <th>Platform</th>
                  <th
                    className="sortable"
                    onClick={() => handleSort('batchPrice')}
                  >
                    <div className="th-content">
                      <span>Batch Price</span>
                      {sortBy === 'batchPrice' && (
                        <span className="sort-indicator">
                          {sortOrder === 'asc' ? ' ↑' : ' ↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th
                    className="sortable"
                    onClick={() => handleSort('batchSize')}
                  >
                    <div className="th-content">
                      <span>Size</span>
                      {sortBy === 'batchSize' && (
                        <span className="sort-indicator">
                          {sortOrder === 'asc' ? ' ↑' : ' ↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th
                    className="sortable"
                    onClick={() => handleSort('totalValue')}
                  >
                    <div className="th-content">
                      <span>Total Value</span>
                      {sortBy === 'totalValue' && (
                        <span className="sort-indicator">
                          {sortOrder === 'asc' ? ' ↑' : ' ↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th>Interval</th>
                  <th
                    className="sortable"
                    onClick={() => handleSort('status')}
                  >
                    <div className="th-content">
                      <span>Status</span>
                      {sortBy === 'status' && (
                        <span className="sort-indicator">
                          {sortOrder === 'asc' ? ' ↑' : ' ↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th>Progress</th>
                  <th>Views</th>
                  <th
                    className="sortable"
                    onClick={() => handleSort('createdAt')}
                  >
                    <div className="th-content">
                      <span>Created</span>
                      {sortBy === 'createdAt' && (
                        <span className="sort-indicator">
                          {sortOrder === 'asc' ? ' ↑' : ' ↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredBatches.map(batch => (
                  <tr
                    key={batch._id}
                    className={selectedBatches.has(batch._id) ? 'selected-row' : ''}
                  >
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedBatches.has(batch._id)}
                        onChange={() => toggleSelectBatch(batch._id)}
                        className="checkbox"
                      />
                    </td>
                    <td>
                      <div className="creator-cell">
                        <a
                          href={getSocialMediaUrl(batch.primaryPlatform, batch.handle)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="creator-link"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <strong>{batch.handle}</strong>
                        </a>
                        {batch.isManual && (
                          <span className="manual-badge">Manual</span>
                        )}
                      </div>
                    </td>
                    <td>{batch.collaborationId?.email || '-'}</td>
                    <td>
                      <span className="platform-badge">
                        {getPlatformIcon(batch.primaryPlatform)} {batch.primaryPlatform}
                      </span>
                    </td>
                    <td>
                      <strong>${batch.batchPrice}</strong>
                      {batch.negotiatedPrice && batch.status === 'negotiating' && (
                        <div className="negotiated-price">
                          Counter: ${batch.negotiatedPrice}
                        </div>
                      )}
                    </td>
                    <td>{batch.batchSize} videos</td>
                    <td className="total-value">
                      <strong>${(batch.batchPrice * batch.batchSize).toLocaleString()}</strong>
                    </td>
                    <td>{batch.batchInterval} days</td>
                    <td>{getStatusBadge(batch.status)}</td>
                    <td>
                      {batch.status === 'accepted' && batch.progressInfo ? (
                        <div className="progress-cell">
                          <div className="progress-text">
                            <span style={{ fontWeight: '600', color: '#374151' }}>
                              {batch.progressInfo.completed}/{batch.progressInfo.total}
                            </span>
                            <span style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                              in progress
                            </span>
                          </div>
                          <div className="progress-bar-container">
                            <div 
                              className="progress-bar" 
                              style={{ 
                                width: `${batch.progressInfo.percentage}%`,
                                backgroundColor: batch.progressInfo.percentage === 100 ? '#10b981' : 
                                                batch.progressInfo.percentage >= 75 ? '#059669' :
                                                batch.progressInfo.percentage >= 50 ? '#3b82f6' :
                                                batch.progressInfo.percentage >= 25 ? '#f59e0b' : '#ef4444'
                              }}
                            ></div>
                          </div>
                          <div className="progress-percentage">
                            {batch.progressInfo.percentage}%
                          </div>
                        </div>
                      ) : (
                        <span style={{ color: '#9ca3af', fontSize: '14px' }}>-</span>
                      )}
                    </td>
                    <td>
                      <div className="views-cell">
                        <div>Avg: {formatNumber(batch.averageCollaborationViews)}</div>
                        <div className="text-secondary">Last: {formatNumber(batch.lastCollaborationViews)}</div>
                      </div>
                    </td>
                    <td>{new Date(batch.createdAt).toLocaleDateString()}</td>
                    <td>
                      <div className="actions-cell">
                        <button
                          className="view-btn"
                          onClick={() => navigate(`/batch/${batch._id}`)}
                          title="View details"
                        >
                          👁️
                        </button>
                        <button
                          className="delete-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteBatch(batch._id);
                          }}
                          title="Delete batch"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default Batch;